import './api';
import React, { useState, useEffect, useRef, useCallback, lazy, Suspense, useMemo } from 'react';
import axios from 'axios';
import io from 'socket.io-client';
import YayCallBanner from './components/YayCallBanner';
import YayCustomerSearchModal from './components/YayCustomerSearchModal';
import YayNotificationToggle from './components/YayNotificationToggle';
import PhoneStatusLights from './components/PhoneStatusLights';
import BackgroundScrapeToast, { BackgroundScrapeChip } from './components/BackgroundScrapeToast';
import ProductGroups from './ProductGroups';
import OEMFormatManager from './OEMFormatManager';
import DataCollectionOverview from './DataCollectionOverview.jsx';
import MakesModelsManager from './MakesModelsManager';
import PartFinderPage from './components/PartFinderPage';
import { themeQuartz, colorSchemeDark } from 'ag-grid-community';
import PermissionsManager from './PermissionsManager';

const KittingManager = lazy(() => import('./KittingManager'));
const KittingComponentSearch = lazy(() => import('./KittingComponentSearch'));
const BtoKittingManager = lazy(() => import('./BtoKitGenerator'));
const SupplierDataIntegration = lazy(() => import('./SupplierDataIntegration'));
const OrderEntry = lazy(() => import('./OrderEntry.jsx'));
const OEMAdvancedSearch = lazy(() => import('./OEMAdvancedSearch'));
const OrderwiseOrderQueue = lazy(() => import('./OrderwiseOrderQueue.jsx'));
const OrderwisePaymentQueue = lazy(() => import('./OrderwisePaymentQueue.jsx'));
const OrderwiseOrderLocks = lazy(() => import('./OrderwiseOrderLocks.jsx'));
const OrderwisePaymentLocks = lazy(() => import('./OrderwisePaymentLocks.jsx'));
const OrderwiseSalesActivity = lazy(() => import('./OrderwiseSalesActivity.jsx'));
const OrderwiseOrdersRequiringPicking = lazy(() => import('./OrderwiseOrdersRequiringPicking.jsx'));
const OrderwiseCustomerPayments = lazy(() => import('./OrderwiseCustomerPayments.jsx'));
const ProductSearch = lazy(() => import('./ProductSearch.jsx'));
const OEMFicheIngestion = lazy(() => import('./features/oem-import/OEMFicheIngestion.jsx'));
const OEMMappingWorkspace = lazy(() => import('./features/oem-import/OEMMappingWorkspaceV2.jsx'));
const YayCallHistory = lazy(() => import('./YayCallHistory.jsx'));
const YaySettings = lazy(() => import('./YaySettings.jsx'));
const PhoneWallboard = lazy(() => import('./PhoneWallboard.jsx'));
const SupplierScrapers = lazy(() => import('./SupplierScrapers.jsx'));
const VehicleLookup = lazy(() => import('./VehicleLookup.jsx'));
const PickSpeedReportPage = lazy(() => import('./features/reports/PickSpeedReportPage.jsx'));
const OrderCheckPage = lazy(() => import('./features/reports/OrderCheckPage.jsx'));
const AdminPricingReportPage = lazy(() => import('./features/reports/AdminPricingReportPage.jsx'));
const PictureBookPage = lazy(() => import('./features/picture-book/PictureBookPage.jsx'));
const DriveChainsPage = lazy(() => import('./features/drive-chains/DriveChainsPage.jsx'));
const SearchMirrorAdmin = lazy(() => import('./features/admin/SearchMirrorAdmin.jsx'));
const PaymentLockNudgeModal = lazy(() => import('./PaymentLockNudgeModal.jsx'));

const FORCE_LOGOUT_EVENT = 'app-force-logout';
const LOGIN_PAGE_URL = '/login';

const normalizeAccountNumber = (value) => (typeof value === 'string' ? value.trim().toUpperCase() : '');
const computeAllowTradePrices = (user, accountNumber) => {
  const isHendlerUser = typeof user === 'string' && user.toLowerCase().includes('@hendler.co.uk');
  const hasAccount = Boolean(normalizeAccountNumber(accountNumber));
  return isHendlerUser || hasAccount;
};
const loadStoredAccountNumber = () => {
  try {
    return localStorage.getItem('accountNumber') || '';
  } catch {
    return '';
  }
};

// Attach JWT token to all API requests
const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
if (token) {
  axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
}
axios.defaults.baseURL = '/'; // Use relative path for API calls over HTTPS

const dispatchForceLogout = (reason) => {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(FORCE_LOGOUT_EVENT, { detail: { reason } }));
};

// Add retry for transient proxy errors (e.g., ECONNREFUSED) and handle 401s
const installAxiosInterceptor = () => {
  if (typeof window === 'undefined') return;
  if (window.__HENDLER_AXIOS_RETRY_INSTALLED) return;
  axios.interceptors.response.use(
    response => response,
    error => {
      const config = error.config || {};
      const message = typeof error.message === 'string' ? error.message : '';
      const isProxyFailure = message.includes('ECONNREFUSED') || message.includes('ECONNRESET');

      if (isProxyFailure) {
        config.__retryCount = (config.__retryCount || 0) + 1;
        if (config.__retryCount <= 3) {
          return new Promise(resolve => {
            setTimeout(() => resolve(axios(config)), 1000);
          });
        }
      }

      if (error.response?.status === 401) {
        // Only force logout for auth-related 401s, not all 401s
        const reason = error.response?.data?.reason;
        const errorMsg = error.response?.data?.error || '';
        const requestUrl = String(config?.url || '').toLowerCase();
        const safeUrl = requestUrl.substring(0, 120);
        const isMeEndpoint = requestUrl.includes('/api/users/me') || requestUrl.includes('/api/user');
        const isAuthFailure = 
          reason === 'disabled' ||
          reason === 'role-changed' ||
          errorMsg.toLowerCase().includes('session expired') ||
          errorMsg.toLowerCase().includes('invalid token') ||
          (isMeEndpoint && errorMsg.toLowerCase().includes('unauthorized'));

        if (isAuthFailure) {
          // Retry /api/users/me once before force-logout (transient 401s)
          if (isMeEndpoint && !config.__401Retried) {
            config.__401Retried = true;
            return new Promise(resolve => {
              setTimeout(() => resolve(axios(config)), 800);
            });
          }
          const baseReason = reason || 'unauthorized';
          dispatchForceLogout(`${baseReason}@${safeUrl}`);
        }
      }

      return Promise.reject(error);
    }
  );
  window.__HENDLER_AXIOS_RETRY_INSTALLED = true;
};

installAxiosInterceptor();



// Capture fatal errors before React renders and show them on-page
const renderGlobalErrorOverlay = (message, stack, meta = {}) => {
  if (typeof document === 'undefined') return;
  if (document.getElementById('global-error-overlay')) return;
  try {
    sessionStorage.setItem('pim_last_fatal_error', JSON.stringify({ message, stack, meta }));
  } catch {}
  const overlay = document.createElement('div');
  overlay.id = 'global-error-overlay';
  overlay.style.position = 'fixed';
  overlay.style.top = '0';
  overlay.style.left = '0';
  overlay.style.right = '0';
  overlay.style.bottom = '0';
  overlay.style.background = 'rgba(0,0,0,0.9)';
  overlay.style.color = '#f5f5f5';
  overlay.style.zIndex = '99999';
  overlay.style.overflow = 'auto';
  overlay.style.padding = '24px';
  overlay.style.fontFamily = 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace';
  const metaText = [
    meta?.filename ? `File: ${meta.filename}` : '',
    Number.isFinite(meta?.lineno) ? `Line: ${meta.lineno}` : '',
    Number.isFinite(meta?.colno) ? `Column: ${meta.colno}` : '',
  ].filter(Boolean).join(' | ');

  // Build overlay content safely using DOM methods (prevent XSS)
  const h2 = document.createElement('h2');
  h2.style.cssText = 'margin: 0 0 12px 0;';
  h2.textContent = 'Fatal error';

  const msgDiv = document.createElement('div');
  msgDiv.style.cssText = 'white-space: pre-wrap; margin-bottom: 16px;';
  msgDiv.textContent = message || 'Unknown error';

  overlay.appendChild(h2);
  overlay.appendChild(msgDiv);

  if (metaText) {
    const metaDiv = document.createElement('div');
    metaDiv.style.cssText = 'margin-bottom: 12px; opacity: 0.8;';
    metaDiv.textContent = metaText;
    overlay.appendChild(metaDiv);
  }

  if (stack) {
    const pre = document.createElement('pre');
    pre.style.cssText = 'white-space: pre-wrap; background: #111; padding: 16px; border-radius: 6px;';
    pre.textContent = stack;
    overlay.appendChild(pre);
  }

  document.body.appendChild(overlay);
};

if (typeof window !== 'undefined' && !window.__HENDLER_GLOBAL_ERROR_INSTALLED) {
  window.__HENDLER_GLOBAL_ERROR_INSTALLED = true;
  try {
    const lastError = sessionStorage.getItem('pim_last_fatal_error');
    if (lastError) {
      const parsed = JSON.parse(lastError);
      renderGlobalErrorOverlay(parsed?.message, parsed?.stack, parsed?.meta || {});
    }
  } catch {}
  window.addEventListener('error', (event) => {
    const err = event?.error;
    const message = err?.message || event?.message || 'Unknown error';
    const stack = err?.stack || '';
    renderGlobalErrorOverlay(message, stack, {
      filename: event?.filename || '',
      lineno: event?.lineno,
      colno: event?.colno,
    });
  });
  window.addEventListener('unhandledrejection', (event) => {
    const reason = event?.reason;
    const message = reason?.message || String(reason || 'Unhandled rejection');
    const stack = reason?.stack || '';
    renderGlobalErrorOverlay(message, stack, {});
  });
}

const getTokenExpiryMs = (jwtToken) => {
  if (!jwtToken) return null;
  try {
    const [, payload] = jwtToken.split('.');
    if (!payload) return null;
    const decodedPayload = (() => {
      if (typeof atob === 'function') return atob(payload);
      if (typeof Buffer !== 'undefined') return Buffer.from(payload, 'base64').toString('utf-8');
      return null;
    })();
    if (!decodedPayload) return null;
    const decoded = JSON.parse(decodedPayload);
    const expSeconds = decoded?.exp;
    return typeof expSeconds === 'number' ? expSeconds * 1000 : null;
  } catch {
    return null;
  }
};

// Error boundary to catch render errors
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Render error caught:', error, errorInfo);
    try {
      if (typeof this.props.onError === 'function') {
        this.props.onError(error, errorInfo);
      }
    } catch {}
    this.setState({ errorInfo });
  }

  componentDidUpdate(prevProps) {
    // Auto-recover when caller changes resetKey (for example, page navigation)
    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false, error: null, errorInfo: null });
    }
  }

  render() {
    if (this.state.hasError) {
      const message = this.state.error?.message || 'Unknown error';
      const stack = this.state.error?.stack || '';
      const componentStack = this.state.errorInfo?.componentStack || '';
      const boundaryName = this.props.boundaryName || 'this section';

      return (
        <div style={{ padding: '2rem', textAlign: 'center' }}>
          <h1>Something went wrong in {boundaryName}: {message}.</h1>
          <p>Details are shown below for troubleshooting.</p>
          {(stack || componentStack) && (
            <div style={{ textAlign: 'left', maxWidth: 900, margin: '1.5rem auto' }}>
              {stack && (
                <>
                  <strong>Error stack</strong>
                  <pre style={{ whiteSpace: 'pre-wrap', background: '#111', color: '#f5f5f5', padding: '1rem', borderRadius: 6 }}>
                    {stack}
                  </pre>
                </>
              )}
              {componentStack && (
                <>
                  <strong>Component stack</strong>
                  <pre style={{ whiteSpace: 'pre-wrap', background: '#111', color: '#f5f5f5', padding: '1rem', borderRadius: 6 }}>
                    {componentStack}
                  </pre>
                </>
              )}
            </div>
          )}
          <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center' }}>
            <button onClick={() => this.setState({ hasError: false, error: null, errorInfo: null })}>Try Again</button>
            <button onClick={() => window.location.reload()}>Reload Page</button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

const MAIN_MENU_CONFIG = [
  { key: 'PartFinder', label: 'PartFinder', copy: 'Look up fitments, OEM references, and part information.' },
  { key: 'Data Collection/Reviewing', label: 'Data Collection', copy: 'Collate OEM information on Product Groups.' },
  { key: 'OEM References & Item Types', label: 'OEM & Item Types', copy: 'Manage Product Groups and OEM Formating rules.' },
  { key: 'OEM Fiche Ingestion', label: 'OEM Fiche Ingestion', copy: 'Automated Fiche Ingestion (Masterpiece)' },
  { key: 'OEM Mapping Workspace', label: 'OEM Mapping Workspace', copy: 'Map Fiche Items to Item Types and Build Kits.' },
  { key: 'OEM Advanced Search', label: 'OEM Advanced Search', copy: 'Search and browse OEM references across the database.' },
  { key: 'Kitting', label: 'Kitting', copy: 'Manage Kitting, BTOKs and components.' },
  { key: 'Makes & Models', label: 'Makes & Models', copy: 'Manage Makes and Models and Data Collection per specific Model' },
  { key: 'Vehicle Lookup', label: 'Vehicle Lookup', copy: 'Look up any UK registration or VIN — combined DVSA MOT history and DVLA tax data.' },
  { key: 'Supplier Data Integration', label: 'Supplier Data', copy: 'Import Supplier Data and Link Models' },
  { key: 'Drive Chain Mapping', label: 'Drive Chain Mapping', copy: 'Map drive chains to models by pitch, links, engine CC and vehicle type.' },
  { key: 'Supplier Scrapers', label: 'Supplier Scrapers', copy: 'Scrape supplier websites for fitment data (NGK, etc.)' },
  { key: 'Orderwise Data', label: 'ERP Integration', copy: 'Manage Orderwise Data' },
  { key: 'Product Search', label: 'Product Search', copy: 'Search across Hendler fitments and Orderwise products.' },
  { key: 'Picture Book', label: 'Picture Book', copy: 'Build the printed A4 product catalogue — layouts, photos, and PDF export.' },
  { key: 'VOIP', label: 'VOIP', copy: 'Call notifications, history, and settings.' },
  { key: 'Reports', label: 'Reports', copy: 'Sales, Warehouse, Stock, and Admin reports.' },
  { key: 'Admin', label: 'Admin', copy: 'Admin - Manage Users' },
  { key: 'Search Mirrors', label: 'Search Mirrors', copy: 'Manage the product & fitment search caches (run + history).' }
];




function SessionTimer({ lastLoginAt }) {
  const [duration, setDuration] = useState('00:00:00');

  useEffect(() => {
    let intervalId = null;
    const parsed = lastLoginAt ? new Date(lastLoginAt).getTime() : NaN;
    const loginTime = Number.isFinite(parsed) ? parsed : Date.now();

    intervalId = setInterval(() => {
      const diff = Date.now() - loginTime;
      if (diff < 0) return;
      const hours = String(Math.floor(diff / 3600000)).padStart(2, '0');
      const minutes = String(Math.floor((diff % 3600000) / 60000)).padStart(2, '0');
      const seconds = String(Math.floor((diff % 60000) / 1000)).padStart(2, '0');
      setDuration(`${hours}:${minutes}:${seconds}`);
    }, 1000);

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [lastLoginAt]);

  return <span className="pim-chip">Session {duration}</span>;
}

export default function App() {
  const consumedPageParamRef = useRef(false);
  const expectedNavRef = useRef(false);
  const previousPageRef = useRef(null);

  // ─── Session & routing state ────────────────────────────────
  const [currentPage, setCurrentPageState] = useState(() => {
    try {
      // Check URL param first (for popup windows)
      const urlParams = new URLSearchParams(window.location.search);
      const pageParam = urlParams.get('page');
      if (pageParam) {
        try {
          sessionStorage.setItem('pim_currentPage', pageParam);
        } catch {}
        return pageParam;
      }
      // Fall back to sessionStorage
      return sessionStorage.getItem('pim_currentPage') || 'Menu';
    } catch {
      return 'Menu';
    }
  });
  // Wrapper to persist page changes to sessionStorage
  const setCurrentPage = useCallback((page) => {
    expectedNavRef.current = true;
    setTimeout(() => { expectedNavRef.current = false; }, 400);
    setCurrentPageState(page);
    try {
      sessionStorage.setItem('pim_currentPage', page);
    } catch {}
  }, []);

  // Consume ?page= once, then remove it from URL to prevent later remounts/reloads
  // from unexpectedly snapping users back to an older page.
  useEffect(() => {
    if (consumedPageParamRef.current) return;
    consumedPageParamRef.current = true;
    try {
      const url = new URL(window.location.href);
      if (!url.searchParams.has('page')) return;
      url.searchParams.delete('page');
      const next = `${url.pathname}${url.search ? `?${url.searchParams.toString()}` : ''}${url.hash || ''}`;
      window.history.replaceState(null, '', next);
    } catch {}
  }, []);
  const [pendingOrderEntryOrderNumber, setPendingOrderEntryOrderNumber] = useState('');
  const [pendingCopyOrderData, setPendingCopyOrderData] = useState(null);
  const [orderEntrySourcePage, setOrderEntrySourcePage] = useState('');
  // VOIP: pending account context for page navigation
  const [pendingOrderEntryAccount, setPendingOrderEntryAccount] = useState('');
  const [pendingCustomerPaymentsAccount, setPendingCustomerPaymentsAccount] = useState('');
  const [pendingOrderEntryViewMode, setPendingOrderEntryViewMode] = useState('');
  const [pendingSalesLedgerAccounts, setPendingSalesLedgerAccounts] = useState('');
  const [pendingSalesActivityAccount, setPendingSalesActivityAccount] = useState('');
  const [pendingSalesActivityDateFrom, setPendingSalesActivityDateFrom] = useState('');
  const [pendingSalesActivityDateTo, setPendingSalesActivityDateTo] = useState('');
  const [ficheWorkspaceContext, setFicheWorkspaceContext] = useState(null);
  const [ficheRunInBackground, setFicheRunInBackground] = useState(() => {
    try {
      return localStorage.getItem('fiche_run_in_background') === 'true';
    } catch {
      return false;
    }
  });
  const [ficheBackgroundProgress, setFicheBackgroundProgress] = useState({
    enabled: false,
    active: false,
    complete: false,
    total: 0,
    current: 0,
    currentTitle: '',
    processedCount: 0,
    failedCount: 0,
    ficheCount: 0,
  });
  const [ficheStopSignal, setFicheStopSignal] = useState(0);
  const [ficheToastMinimized, setFicheToastMinimized] = useState(() => {
    try {
      return localStorage.getItem('fiche_background_toast_minimized') === 'true';
    } catch {
      return false;
    }
  });
  const persistFicheToastMinimized = useCallback((next) => {
    setFicheToastMinimized(Boolean(next));
    try {
      localStorage.setItem('fiche_background_toast_minimized', next ? 'true' : 'false');
    } catch {}
  }, []);

  // ─── VOIP state ───────────────────────────────────────────
  const [yayNotificationsEnabled, setYayNotificationsEnabled] = useState(true);
  const yaySocketRef = useRef(null);
  const [yaySocket, setYaySocket] = useState(null);
  const [voipCustomerSearch, setVoipCustomerSearch] = useState(null); // { phone, callId, onLink }

  const username = localStorage.getItem('username') || '';
  const [isDark, setIsDark] = useState(localStorage.getItem('isDark') === 'true');
  const [hasToken, setHasToken] = useState(() => Boolean(localStorage.getItem('token')));
  const gridTheme = isDark ? themeQuartz.withPart(colorSchemeDark) : themeQuartz;
  const [accountNumber, setAccountNumber] = useState(() => loadStoredAccountNumber());
  const [currentUserProfile, setCurrentUserProfile] = useState(null);
  const allowTradePrices = useMemo(
    () => computeAllowTradePrices(username, accountNumber),
    [username, accountNumber]
  );
  const pendingLogoutRef = useRef(false);

  const persistAccountNumber = useCallback((value) => {
    const nextValue = value || '';
    setAccountNumber(nextValue);
    try {
      if (nextValue) {
        localStorage.setItem('accountNumber', nextValue);
      } else {
        localStorage.removeItem('accountNumber');
      }
    } catch (err) {
      console.warn('Failed to persist account number', err);
    }
  }, []);

  const forceLogout = useCallback((reason = 'manual') => {
    if (pendingLogoutRef.current) return;
    pendingLogoutRef.current = true;
    console.info('Forcing logout', { reason });
    try {
      localStorage.removeItem('token');
      localStorage.removeItem('username');
      localStorage.removeItem('role');
      localStorage.removeItem('permissions');
      localStorage.removeItem('loginTime');
      localStorage.removeItem('accountNumber');
    } catch (err) {
      console.warn('Failed to clear auth storage', err);
    }
    setAccountNumber('');
    delete axios.defaults.headers.common['Authorization'];
    setHasToken(false);
    window.location.href = LOGIN_PAGE_URL;
  }, []);

  const enforceAuth = useCallback(() => {
    const storedToken = localStorage.getItem('token');
    const tokenExists = Boolean(storedToken);
    // Only update state if actually changed to avoid triggering effects
    setHasToken(prev => prev !== tokenExists ? tokenExists : prev);
    if (!storedToken) {
      forceLogout('missing-token');
      return false;
    }
    const expiresAt = getTokenExpiryMs(storedToken);
    if (expiresAt && expiresAt <= Date.now()) {
      forceLogout('expired-token');
      return false;
    }
    return true;
  }, [forceLogout]);

  useEffect(() => {
    const handleForceLogout = (event) => {
      forceLogout(event?.detail?.reason || 'signal');
    };
    window.addEventListener(FORCE_LOGOUT_EVENT, handleForceLogout);
    return () => window.removeEventListener(FORCE_LOGOUT_EVENT, handleForceLogout);
  }, [forceLogout]);

  useEffect(() => {
    if (pendingLogoutRef.current) return undefined;
    enforceAuth();
    return undefined;
  }, [enforceAuth]);

  useEffect(() => {
    const handleStorageChange = (event) => {
      if (event.key !== 'token') return;
      if (!event.newValue) {
        forceLogout('storage-cleared');
        return;
      }
      axios.defaults.headers.common['Authorization'] = `Bearer ${event.newValue}`;
      setHasToken(true);
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [forceLogout]);

  // Periodic token expiry check to prevent 401s on API calls
  useEffect(() => {
    const intervalId = setInterval(() => {
      if (pendingLogoutRef.current) return;
      if (currentPage === 'OEM Mapping Workspace') return;
      enforceAuth();
    }, 60000); // Check every minute
    return () => clearInterval(intervalId);
  }, [enforceAuth, currentPage]);

  useEffect(() => {
    if (!hasToken && !pendingLogoutRef.current) {
      forceLogout('state-missing-token');
    }
  }, [hasToken, forceLogout]);

  useEffect(() => {
    if (pendingLogoutRef.current) return;
    enforceAuth();
  }, [currentPage, enforceAuth]);

  // Temporary hardening: if we're on OEM Mapping Workspace and an unexpected
  // page change occurs without explicit navigation intent, keep the user on mapping.
  useEffect(() => {
    const prev = previousPageRef.current;
    if (
      prev === 'OEM Mapping Workspace' &&
      currentPage !== 'OEM Mapping Workspace' &&
      !expectedNavRef.current &&
      !pendingLogoutRef.current
    ) {
      setCurrentPageState('OEM Mapping Workspace');
      try {
        sessionStorage.setItem('pim_currentPage', 'OEM Mapping Workspace');
      } catch {}
      return;
    }
    previousPageRef.current = currentPage;
  }, [currentPage]);

  const navigateToPage = useCallback((page) => {
    if (!enforceAuth()) return;
    setCurrentPage(page);
  }, [enforceAuth]);

  const openOrderEntryForOrderNumber = useCallback((orderNumber) => {
    const orderNo = String(orderNumber || '').trim();
    if (!orderNo) return;
    if (!enforceAuth()) return;
    setOrderEntrySourcePage(currentPage);
    setPendingOrderEntryOrderNumber(orderNo);
    setCurrentPage('Order Entry');
  }, [enforceAuth, currentPage]);

  // Open Order Entry with copy data pre-populated (for Copy Order feature)
  const openOrderEntryWithCopyData = useCallback((copyData) => {
    if (!copyData) return;
    if (!enforceAuth()) return;
    setOrderEntrySourcePage(currentPage);
    setPendingCopyOrderData(copyData);
    setCurrentPage('Order Entry');
  }, [enforceAuth, currentPage]);

  const openFicheWorkspaceForModel = useCallback((modelKey) => {
    setFicheWorkspaceContext({ modelKey });
    setCurrentPage('OEM Mapping Workspace');
  }, []);

  const refreshCurrentUserProfile = useCallback(async () => {
    try {
      const { data } = await axios.get('/api/users/me');
      if (data && Object.prototype.hasOwnProperty.call(data, 'AccountNumber')) {
        persistAccountNumber(data.AccountNumber || '');
      }
      setCurrentUserProfile(data || null);
      return data || null;
    } catch (err) {
      console.warn('Failed to fetch current user profile', err);
      setCurrentUserProfile(null);
      return null;
    }
  }, [persistAccountNumber]);

  const isOrderwiseStaff = useMemo(() => {
    const value = currentUserProfile?.OrderwiseUserId;
    const id = value == null ? null : Number(value);
    return Number.isFinite(id) && id > 0;
  }, [currentUserProfile]);

useEffect(() => {
  document.documentElement.classList.toggle('dark', isDark);
  localStorage.setItem('isDark', isDark.toString());
}, [isDark]);

  useEffect(() => {
    refreshCurrentUserProfile();
  }, [refreshCurrentUserProfile]);

  const [permissions, setPermissions] = useState({});
  const visibleMenuItems = useMemo(
    () => MAIN_MENU_CONFIG.filter(item => permissions?.[item.key] && permissions[item.key] !== 'none'),
    [permissions]
  );

  const header = (
    <header className="pim-topbar">
      <div className="pim-topbar__cluster">
        <button type="button" onClick={() => navigateToPage('Menu')}>
          Menu
        </button>
        <span className="pim-chip pim-chip--accent">Hendler PIM</span>
        <div className="pim-topbar__user">
          <span className="pim-chip">{username || 'Unknown user'}</span>
          <span className="pim-chip">Acct {accountNumber || '—'}</span>
          <SessionTimer lastLoginAt={currentUserProfile?.LastLoginAt} />
        </div>
      </div>

      <div className="pim-topbar__actions">
        <BackgroundScrapeChip
          progress={ficheBackgroundProgress}
          minimized={ficheToastMinimized}
          onRestore={() => persistFicheToastMinimized(false)}
        />
        {isOrderwiseStaff && permissions['VOIP'] && permissions['VOIP'] !== 'none' && (
          <PhoneStatusLights socket={yaySocket} isDark={isDark} />
        )}
        <YayNotificationToggle
          enabled={yayNotificationsEnabled}
          onToggle={setYayNotificationsEnabled}
          isStaff={isOrderwiseStaff && permissions['VOIP'] && permissions['VOIP'] !== 'none'}
          isDark={isDark}
        />
        {hasToken ? (
          <button type="button" onClick={() => forceLogout('manual')}>
            Logout
          </button>
        ) : (
          <button type="button" onClick={() => window.location.href = LOGIN_PAGE_URL}>
            Login
          </button>
        )}
        <span className="pim-chip pim-chip--accent">Mode: {isDark ? 'Dark' : 'Light'}</span>
        <div className="pim-topbar__toggles">
          <button type="button" onClick={() => setIsDark(prev => !prev)}>
            Toggle Mode
          </button>
        </div>
      </div>
    </header>
  );

useEffect(() => {
  const fetchPermissions = async () => {
    try {
      const userResponse = await axios.get('/api/users/me');
      const serverAccount = normalizeAccountNumber(
        userResponse.data?.AccountNumber || userResponse.data?.accountNumber
      );
      if (serverAccount) {
        persistAccountNumber(serverAccount);
      }

      // Fetch permissions
      const res = await axios.get('/api/permissions/user');
      const perms = res.data && typeof res.data === 'object' ? res.data : {};
      setPermissions(perms);
    } catch (err) {
      setPermissions({});
    }
  };

  fetchPermissions();
}, [persistAccountNumber]);

  // ─── VOIP: Socket.IO connection ──────────────────────────────
  useEffect(() => {
    const voipPermission = permissions['VOIP'];
    if (!voipPermission || voipPermission === 'none' || !isOrderwiseStaff) {
      // No VOIP access — tear down any existing socket
      if (yaySocketRef.current) {
        yaySocketRef.current.disconnect();
        yaySocketRef.current = null;
        setYaySocket(null);
      }
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) return;

    // Only create if not already connected
    if (yaySocketRef.current?.connected) return;

    const sock = io(window.location.origin, {
      auth: { token },
      transports: ['polling', 'websocket'],  // polling first (works through any reverse proxy), upgrades to WS if available
      reconnectionAttempts: 10,
      reconnectionDelay: 3000,
    });

    sock.on('connect', () => {
      console.info('[VOIP Socket] Connected');
    });
    sock.on('connect_error', (err) => {
      console.warn('[VOIP Socket] Connection error:', err.message);
    });

    yaySocketRef.current = sock;
    setYaySocket(sock); // trigger re-render so YayCallBanner receives the socket

    return () => {
      sock.disconnect();
      yaySocketRef.current = null;
      setYaySocket(null);
    };
  }, [permissions, isOrderwiseStaff]);

  // ─── VOIP: Fetch notification preference ─────────────────────
  useEffect(() => {
    const voipPermission = permissions['VOIP'];
    if (!voipPermission || voipPermission === 'none' || !isOrderwiseStaff) return;
    axios.get('/api/yay/notifications')
      .then((res) => {
        if (typeof res.data?.enabled === 'boolean') {
          setYayNotificationsEnabled(res.data.enabled);
        }
      })
      .catch(() => { /* ignore — defaults to true */ });
  }, [permissions, isOrderwiseStaff]);

  // ─── VOIP: Navigation callbacks ──────────────────────────────
  const handleVoipNewOrder = useCallback((accountNumber) => {
    if (!enforceAuth()) return;
    setOrderEntrySourcePage(currentPage);
    if (accountNumber) setPendingOrderEntryAccount(accountNumber);
    setPendingOrderEntryViewMode('');  // Explicitly clear so New Order doesn't land on Sales Ledger tab
    setPendingSalesLedgerAccounts('');
    setCurrentPage('Order Entry');
  }, [enforceAuth, currentPage]);

  const handleVoipSalesLedger = useCallback((allAccounts) => {
    if (!enforceAuth()) return;
    setOrderEntrySourcePage(currentPage);
    // allAccounts may be comma-separated for multi-account; use first for OrderEntry context
    const firstAccount = (allAccounts || '').split(',')[0].trim();
    if (firstAccount) setPendingOrderEntryAccount(firstAccount);
    setPendingSalesLedgerAccounts(allAccounts || '');
    setPendingOrderEntryViewMode('salesLedger');
    setCurrentPage('Order Entry');
  }, [enforceAuth, currentPage]);

  const handleVoipPayments = useCallback((allAccounts) => {
    if (!enforceAuth()) return;
    if (allAccounts) setPendingCustomerPaymentsAccount(allAccounts);
    setCurrentPage('Customer Payments');
  }, [enforceAuth]);

  const handleVoipSalesActivity = useCallback((allAccounts) => {
    if (!enforceAuth()) return;
    if (allAccounts) {
      setPendingSalesActivityAccount(allAccounts);
      // Rolling 3-month window (faster than 12 months)
      const now = new Date();
      const threeMonthsAgo = new Date(now);
      threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);
      const pad = (n) => String(n).padStart(2, '0');
      setPendingSalesActivityDateFrom(`${threeMonthsAgo.getFullYear()}-${pad(threeMonthsAgo.getMonth() + 1)}-${pad(threeMonthsAgo.getDate())}`);
      setPendingSalesActivityDateTo(`${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`);
    }
    setCurrentPage('Sales Activity');
  }, [enforceAuth]);

  const handleVoipSearchCustomer = useCallback((phone, callId, linkCallback) => {
    if (!enforceAuth()) return;
    setVoipCustomerSearch({ phone, callId, onLink: linkCallback, mode: 'link' });
  }, [enforceAuth]);

  // State for standalone phone mapping manager (no active call context)
  const [showPhoneMappingManager, setShowPhoneMappingManager] = useState(false);



  return (
    <ErrorBoundary boundaryName="App" resetKey={currentPage}>
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>

      {header}

      {/* VOIP Call Banner (global, for staff with VOIP permission) */}
      <YayCallBanner
        socket={yaySocket}
        isStaff={isOrderwiseStaff}
        notificationsEnabled={yayNotificationsEnabled}
        userKey={currentUserProfile?.UserKey}
        onNewOrder={handleVoipNewOrder}
        onSalesLedger={handleVoipSalesLedger}
        onSalesActivity={handleVoipSalesActivity}
        onPayments={handleVoipPayments}
        onSearchCustomer={handleVoipSearchCustomer}
        isDark={isDark}
      />

      {/* VOIP Customer Search Modal (link unknown caller to customer) */}
      {voipCustomerSearch && (
        <YayCustomerSearchModal
          mode={voipCustomerSearch.mode || 'link'}
          phone={voipCustomerSearch.phone}
          callId={voipCustomerSearch.callId}
          onLink={voipCustomerSearch.onLink}
          onClose={() => setVoipCustomerSearch(null)}
          isDark={isDark}
        />
      )}

      {/* Standalone Phone Mapping Manager */}
      {showPhoneMappingManager && (
        <YayCustomerSearchModal
          mode="manage"
          onClose={() => setShowPhoneMappingManager(false)}
          isDark={isDark}
        />
      )}

      {/* Menu screen */}
{currentPage === 'Menu' && (
  <section className="pim-menu" aria-label="Main menu">
    <div className="pim-menu-hero">
      <p className="pim-menu-badge">Hendler PIM</p>
      <h2 className="pim-menu-title">Main Menu</h2>
      <p className="pim-menu-copy">
        Gain access to various modules, such as Partfinder, Data Collection, OEM References, Kitting, and more.
      </p>
      <p className="pim-menu-hero-footnote">Modules appear according to your account's permissions.</p>
    </div>
    {visibleMenuItems.length === 0 ? (
      <p className="pim-menu-empty">No modules assigned to your role.</p>
    ) : (
      <div className="pim-menu-grid">
        {visibleMenuItems.map((item, index) => (
          <button
            key={item.key}
            type="button"
            className="pim-menu-card"
            onClick={() => setCurrentPage(item.key)}
          >
            <span className="pim-menu-card__index">{String(index + 1).padStart(2, '0')}</span>
            <div className="pim-menu-card__body">
              <span className="pim-menu-card__title">{item.label}</span>
              {item.copy && <span className="pim-menu-card__copy">{item.copy}</span>}
            </div>
            <span className="pim-menu-card__chevron" aria-hidden="true">→</span>
          </button>
        ))}
      </div>
    )}
  </section>
)}

      {/* PartFinder screen (shared component) */}
      {currentPage === 'PartFinder' && permissions['PartFinder'] !== 'none' && (
        <div style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
          <PartFinderPage
            permissions={permissions}
            isDark={isDark}
            allowTradePrices={allowTradePrices}
          />
        </div>
      )}
      {currentPage === 'PartFinder' && permissions['PartFinder'] === 'none' && (
        <div>Unauthorized access to PartFinder.</div>
      )}
      

      {/* Data Collection / Reviewing */}
      {currentPage === 'Data Collection/Reviewing' && permissions['Data Collection/Reviewing'] !== 'none' && (
        <section className="pim-section" aria-label="Data collection workspace" style={{ overflow: 'hidden', padding: '0.75rem clamp(1.5rem, 3vw, 3rem) 0', flex: 1, minHeight: 0 }}>
          <div className="pim-section__card" style={{ padding: '0.75rem clamp(1.25rem, 2vw, 2rem)', gap: '0.5rem' }}>
            <div className="pim-section__header">
              <div>
                <p className="pim-chip">Data collection</p>
                <h2>Review Product Groups</h2>
                <p className="pim-section__helper">
                  Collect OEM Data By Product Groups - IE Electrical, Wheels, Calipers.
                </p>
              </div>
              <div className="pim-section__actions">
                <button type="button" onClick={() => setCurrentPage('Menu')}>
                  ← Back to Menu
                </button>
              </div>
            </div>
            <div className="pim-section__grid">
              <DataCollectionOverview permissions={permissions} isDark={isDark} />
            </div>
          </div>
        </section>
      )}
      {currentPage === 'Data Collection/Reviewing' && permissions['Data Collection/Reviewing'] === 'none' && (
      <div>Unauthorized access to Data Collection/Reviewing.</div>
      )}

      {/* OEM References & Item Types */}
      {currentPage === 'OEM References & Item Types' && permissions['OEM References & Item Types'] !== 'none' && (
        <div style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
          <div style={{ padding: '2rem' }}>
            <div style={{ marginTop: '1rem' }}>
              <button onClick={() => setCurrentPage('Product Groups')}>
                Product Groups
              </button>
                          <div style={{ marginTop: '1rem' }}>
              <button onClick={() => setCurrentPage('OEM Format Manager')}>
                OEM Format Rules
              </button>
            </div>
            <div style={{ marginTop: '1rem' }}>
              <button onClick={() => setCurrentPage('OEM Advanced Search')}>
                OEM Advanced Search
              </button>
            </div>
            </div>
          </div>
        </div>
      )}


      

      {/* Product Groups screen */}
      {currentPage === 'Product Groups' && permissions['Product Groups'] !== 'none' && (
        <div style={{ overflow: 'hidden', flex: 1, minHeight: 0 }}>
            <ProductGroups permissions={permissions} theme={gridTheme} />
        </div>
      )}
                  {currentPage === 'Product Groups' && permissions['Product Groups'] === 'none' && (
      <div>Unauthorized access to Product Groups.</div>
      )}
        {/* OEM Format Manager screen */}
            {currentPage === 'OEM Format Manager' && permissions['OEM References & Item Types'] !== 'none' && (
        <div style={{ padding: '0.75rem 2rem 0', display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
          <OEMFormatManager permissions={permissions} theme={gridTheme} />
          <div style={{ marginTop: '0.5rem' }}>
            <button onClick={() => setCurrentPage('OEM References & Item Types')}>← Back</button>
          </div>
        </div>
      )}

      {currentPage === 'OEM Advanced Search' && permissions['OEM Advanced Search'] !== 'none' && (
        <div style={{ padding: 0, flex: 1, minHeight: 0, overflow: 'auto' }}>
          <ErrorBoundary
            boundaryName="OEM Advanced Search"
            resetKey={currentPage}
            onError={(error, errorInfo) => {
              console.error('OEM Advanced Search boundary caught an error', {
                page: currentPage,
                message: error?.message,
                stack: error?.stack,
                componentStack: errorInfo?.componentStack,
              });
            }}
          >
            <Suspense fallback={<div style={{ padding: '2rem' }}>Loading OEM search…</div>}>
              <OEMAdvancedSearch permissions={permissions} isDark={isDark} />
            </Suspense>
          </ErrorBoundary>
          <button
            type="button"
            className="pim-topbar-button pim-floating-back"
            onClick={() => setCurrentPage('Menu')}
          >
            {'\u2190 Back'}
          </button>
        </div>
      )}

{/* Makes & Models */}

{currentPage === 'Makes & Models' && permissions['Makes & Models'] !== 'none' && (
  <div style={{ padding: '0.75rem 2rem 0', display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
      <h2 style={{ margin: 0 }}>Makes & Models Management</h2>
      <button onClick={() => setCurrentPage('Menu')}>← Back to Menu</button>
    </div>
    <MakesModelsManager permissions={permissions} isDark={isDark} onOpenFicheWorkspace={openFicheWorkspaceForModel} />
  </div>
)}
{currentPage === 'Makes & Models' && permissions['Makes & Models'] === 'none' && (
  <div>Unauthorized access to Makes & Models.</div>
)}




{/* Vehicle Lookup */}
{currentPage === 'Vehicle Lookup' && permissions['Vehicle Lookup'] !== 'none' && (
  <div style={{ padding: '0.75rem 2rem 0', display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
      <h2 style={{ margin: 0 }}>Vehicle Lookup</h2>
      <button onClick={() => setCurrentPage('Menu')}>← Back to Menu</button>
    </div>
    <Suspense fallback={<div style={{ padding: '2rem' }}>Loading vehicle lookup…</div>}>
      <VehicleLookup permissions={permissions} isDark={isDark} />
    </Suspense>
  </div>
)}
{currentPage === 'Vehicle Lookup' && permissions['Vehicle Lookup'] === 'none' && (
  <div>Unauthorized access to Vehicle Lookup.</div>
)}




{/* Supplier Data Integration */}
{currentPage === 'Supplier Data Integration' && permissions['Makes Models'] !== 'none' && (
  <Suspense fallback={<div style={{ padding: '2rem' }}>Loading supplier tools…</div>}>
    <SupplierDataIntegration permissions={permissions} isDark={isDark} />
  </Suspense>
)}
{currentPage === 'Supplier Data Integration' && permissions['Makes Models'] === 'none' && (
  <div>Unauthorized access to Supplier Data Integration.</div>
)}

{/* Supplier Scrapers */}
{currentPage === 'Supplier Scrapers' && permissions['Supplier Scrapers'] !== 'none' && (
  <Suspense fallback={<div style={{ padding: '2rem' }}>Loading supplier scrapers…</div>}>
    <SupplierScrapers permissions={permissions} isDark={isDark} />
  </Suspense>
)}
{currentPage === 'Supplier Scrapers' && permissions['Supplier Scrapers'] === 'none' && (
  <div>Unauthorized access to Supplier Scrapers.</div>
)}

{/* Picture Book (printed catalogue builder) */}
{currentPage === 'Picture Book' && permissions['Picture Book'] !== 'none' && (
  // overflow:hidden (not auto): the outer shell must not scroll. A few pixels of
  // content-height jitter on tab switches was bouncing the whole page including
  // the Back to Menu row. Each tab scrolls inside its own pane instead.
  <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
    <div style={{ padding: '0.5rem 1rem 0', flex: '0 0 auto' }}>
      <button onClick={() => setCurrentPage('Menu')}>← Back to Menu</button>
    </div>
    <div style={{ flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
      <Suspense fallback={<div style={{ padding: '2rem' }}>Loading picture book…</div>}>
        <PictureBookPage permissions={permissions} isDark={isDark} />
      </Suspense>
    </div>
  </div>
)}
{currentPage === 'Picture Book' && permissions['Picture Book'] === 'none' && (
  <div>Unauthorized access to Picture Book.</div>
)}

{/* Drive Chain Mapping */}
{currentPage === 'Drive Chain Mapping' && permissions['Drive Chain Mapping'] !== 'none' && (
  <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
    <div style={{ padding: '0.5rem 1rem 0', flex: '0 0 auto' }}>
      <button onClick={() => setCurrentPage('Menu')}>← Back to Menu</button>
    </div>
    <div style={{ flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
      <Suspense fallback={<div style={{ padding: '2rem' }}>Loading drive chains…</div>}>
        <DriveChainsPage permissions={permissions} isDark={isDark} />
      </Suspense>
    </div>
  </div>
)}
{currentPage === 'Drive Chain Mapping' && permissions['Drive Chain Mapping'] === 'none' && (
  <div>Unauthorized access to Drive Chain Mapping.</div>
)}

      {currentPage === 'Kitting' && permissions['Kitting'] !== 'none' && (
        <div style={{ padding: '2rem', flex: 1, minHeight: 0, overflow: 'auto' }}>
          <h2>Kitting</h2>
          <p style={{ maxWidth: '48rem' }}>
            Manage multi-component OEM kits and build-to-order kits. Access tools to create, edit, and search for kits and their components.
          </p>
          <div style={{ marginTop: '1rem' }}>
            <div style={{ margin: '0.5rem 0' }}>
              <button onClick={() => setCurrentPage('Kitting Manager')}>
                Manage Kits
              </button>
            </div>
            <div style={{ margin: '0.5rem 0' }}>
              <button onClick={() => setCurrentPage('BTO Kitting Manager')}>
                Build-To-Order Kits
              </button>
            </div>
            <div style={{ margin: '0.5rem 0' }}>
              <button onClick={() => setCurrentPage('Kitting Component Search')}>
                Component Search
              </button>
            </div>
          </div>
          <div style={{ marginTop: '1rem' }}>
            <button onClick={() => setCurrentPage('Menu')}>← Back to Menu</button>
          </div>
        </div>
      )}
      {currentPage === 'Kitting' && permissions['Kitting'] === 'none' && (
        <div>Unauthorized access to Kitting.</div>
      )}

      {currentPage === 'Kitting Manager' && permissions['Kitting'] !== 'none' && (
        <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
          <Suspense fallback={<div style={{ padding: '2rem' }}>Loading kitting manager…</div>}>
            <KittingManager permissions={permissions} isDark={isDark} />
          </Suspense>
          <div style={{ padding: '0 1.25rem 0.5rem' }}>
            <button onClick={() => setCurrentPage('Kitting')}>← Back to Kitting</button>
          </div>
        </div>
      )}
      {currentPage === 'Kitting Manager' && permissions['Kitting'] === 'none' && (
        <div>Unauthorized access to Kitting Manager.</div>
      )}

      {currentPage === 'BTO Kitting Manager' && permissions['Kitting'] !== 'none' && (
        <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
          <Suspense fallback={<div style={{ padding: '2rem' }}>Loading build-to-order tools…</div>}>
            <BtoKittingManager permissions={permissions} isDark={isDark} />
          </Suspense>
          <div style={{ padding: '0 1.25rem 0.5rem' }}>
            <button onClick={() => setCurrentPage('Kitting')}>← Back to Kitting</button>
          </div>
        </div>
      )}
      {currentPage === 'BTO Kitting Manager' && permissions['Kitting'] === 'none' && (
        <div>Unauthorized access to Build-To-Order Kits.</div>
      )}

      {currentPage === 'Kitting Component Search' && permissions['Kitting'] !== 'none' && (
        <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
          <Suspense fallback={<div style={{ padding: '2rem' }}>Loading component search…</div>}>
            <KittingComponentSearch permissions={permissions} isDark={isDark} />
          </Suspense>
          <div style={{ padding: '0 1.25rem 0.5rem' }}>
            <button onClick={() => setCurrentPage('Kitting')}>← Back to Kitting</button>
          </div>
        </div>
      )}
      {currentPage === 'Kitting Component Search' && permissions['Kitting'] === 'none' && (
        <div>Unauthorized access to Kit Component Search.</div>
      )}

      {/* Orderwise Data (sub-menu) */}
      {currentPage === 'Orderwise Data' && permissions['Orderwise Data'] !== 'none' && (
        <div style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
          <div style={{ padding: '2rem' }}>
            <h2>Orderwise data</h2>

<div style={{ marginTop: '1rem' }}>
  <div style={{ margin: '0.5rem 0' }}>
    <button onClick={() => setCurrentPage('Order Entry')}>
      Customer Actions
    </button>
  </div>
  <div style={{ margin: '0.5rem 0' }}>
    <button onClick={() => setCurrentPage('Sales Activity')}>
      Sales Activity
    </button>
  </div>
  <div style={{ margin: '0.5rem 0' }}>
    <button onClick={() => setCurrentPage('Orders Requiring Picking')}>
      Orders Requiring Picking
    </button>
  </div>
  {isOrderwiseStaff ? (
    <div style={{ margin: '0.5rem 0' }}>
      <button onClick={() => setCurrentPage('Order Queue')}>
        Order Queue
      </button>
    </div>
  ) : null}
  <div style={{ margin: '0.5rem 0' }}>
    <button onClick={() => setCurrentPage('Customer Payments')}>
      Customer Payments
    </button>
  </div>
  {permissions['Admin'] !== 'none' ? (
    <div style={{ margin: '0.5rem 0' }}>
      <button onClick={() => setCurrentPage('Payment Queue')}>
        Payment Queue
      </button>
    </div>
  ) : null}
  {isOrderwiseStaff ? (
    <div style={{ margin: '0.5rem 0' }}>
      <button onClick={() => setCurrentPage('Order Locks')}>
        Order Locks
      </button>
    </div>
  ) : null}
  {isOrderwiseStaff || permissions['Admin'] !== 'none' ? (
    <div style={{ margin: '0.5rem 0' }}>
      <button onClick={() => setCurrentPage('Payment Locks')}>
        Payment Locks
      </button>
    </div>
  ) : null}
</div>

            <div style={{ marginTop: '1rem' }}>
              <button onClick={() => setCurrentPage('Menu')}>← Back to Main Menu</button>
           </div>
          </div>
        </div>
      )}
      {currentPage === 'Orderwise Data' && permissions['Orderwise Data'] === 'none' && (
        <div>Unauthorized access to Orderwise Data.</div>
      )}

      {/* Order Entry (child of Orderwise Data) */}
      {currentPage === 'Order Entry' && permissions['Orderwise Data'] !== 'none' && (
        <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
          <div style={{ padding: '0.5rem 2rem 0' }}>
            <button onClick={() => setCurrentPage('Orderwise Data')}>← Back to Orderwise data</button>
          </div>
          <Suspense fallback={<div style={{ padding: '2rem' }}>Loading order entry…</div>}> 
            <OrderEntry
              isDark={isDark}
              permissions={permissions}
              allowTradePrices={allowTradePrices}
              currentUserOrderwiseUserId={currentUserProfile?.OrderwiseUserId}
              isOrderwiseStaff={isOrderwiseStaff}
              initialOrderNumber={pendingOrderEntryOrderNumber}
              onInitialOrderConsumed={() => setPendingOrderEntryOrderNumber('')}
              initialAccountNumber={pendingOrderEntryAccount}
              onInitialAccountConsumed={() => setPendingOrderEntryAccount('')}
              initialCopyData={pendingCopyOrderData}
              onCopyDataConsumed={() => setPendingCopyOrderData(null)}
              initialViewMode={pendingOrderEntryViewMode}
              onInitialViewModeConsumed={() => setPendingOrderEntryViewMode('')}
              initialSalesLedgerAccounts={pendingSalesLedgerAccounts}
              onSalesLedgerAccountsConsumed={() => setPendingSalesLedgerAccounts('')}
              onReturnToSource={() => {
                const source = orderEntrySourcePage;
                setOrderEntrySourcePage('');
                if (source) setCurrentPage(source);
              }}
            />
          </Suspense>
        </div>
      )}
      {currentPage === 'Order Entry' && permissions['Orderwise Data'] === 'none' && (
        <div>Unauthorized access to Order Entry.</div>
      )}



      {/* Order Queue (child of Orderwise Data) */}
      {currentPage === 'Order Queue' && permissions['Orderwise Data'] !== 'none' && (
        isOrderwiseStaff ? (
          <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
            <div style={{ padding: '0.5rem 2rem 0' }}>
              <button onClick={() => setCurrentPage('Orderwise Data')}>← Back to Orderwise data</button>
            </div>
            <Suspense fallback={<div style={{ padding: '2rem' }}>Loading order queue…</div>}>
              <OrderwiseOrderQueue isDark={isDark} permissions={permissions} />
            </Suspense>
          </div>
        ) : (
          <div>Unauthorized access to Order Queue.</div>
        )
      )}
      {currentPage === 'Order Queue' && permissions['Orderwise Data'] === 'none' && (
        <div>Unauthorized access to Order Queue.</div>
      )}


      {/* Payment Queue (child of Orderwise Data, admin-only) */}
      {currentPage === 'Payment Queue' && permissions['Orderwise Data'] !== 'none' && (
        permissions['Admin'] !== 'none' ? (
          <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
            <div style={{ padding: '0.5rem 2rem 0' }}>
              <button onClick={() => setCurrentPage('Orderwise Data')}>← Back to Orderwise data</button>
            </div>
            <Suspense fallback={<div style={{ padding: '2rem' }}>Loading payment queue…</div>}>
              <OrderwisePaymentQueue isDark={isDark} permissions={permissions} />
            </Suspense>
          </div>
        ) : (
          <div style={{ padding: '2rem' }}>Unauthorized access to Payment Queue.</div>
        )
      )}
      {currentPage === 'Payment Queue' && permissions['Orderwise Data'] === 'none' && (
        <div>Unauthorized access to Payment Queue.</div>
      )}

      {/* Sales Activity (child of Orderwise Data) */}
      {currentPage === 'Sales Activity' && permissions['Orderwise Data'] !== 'none' && (
        <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
          <div style={{ padding: '0.5rem 2rem 0' }}>
            <button onClick={() => setCurrentPage('Orderwise Data')}>← Back to Orderwise data</button>
          </div>
          <Suspense fallback={<div style={{ padding: '2rem' }}>Loading sales activity…</div>}>
            <OrderwiseSalesActivity
              isDark={isDark}
              onOpenOrder={openOrderEntryForOrderNumber}
              onCopyOrder={openOrderEntryWithCopyData}
              isOrderwiseStaff={isOrderwiseStaff}
              canPickReview={permissions['Other Reports'] !== 'none'}
              initialAccountFilter={pendingSalesActivityAccount}
              initialDateFrom={pendingSalesActivityDateFrom}
              initialDateTo={pendingSalesActivityDateTo}
              onInitialFiltersConsumed={() => {
                setPendingSalesActivityAccount('');
                setPendingSalesActivityDateFrom('');
                setPendingSalesActivityDateTo('');
              }}
            />
          </Suspense>
        </div>
      )}
      {currentPage === 'Sales Activity' && permissions['Orderwise Data'] === 'none' && (
        <div>Unauthorized access to Sales Activity.</div>
      )}

      {/* Orders Requiring Picking (child of Orderwise Data) */}
      {currentPage === 'Orders Requiring Picking' && permissions['Orderwise Data'] !== 'none' && (
        <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
          <div style={{ padding: '0.5rem 2rem 0' }}>
            <button onClick={() => setCurrentPage('Orderwise Data')}>← Back to Orderwise data</button>
          </div>
          <Suspense fallback={<div style={{ padding: '2rem' }}>Loading picking orders…</div>}>
            <OrderwiseOrdersRequiringPicking isDark={isDark} onOpenOrder={openOrderEntryForOrderNumber} isOrderwiseStaff={isOrderwiseStaff} />
          </Suspense>
        </div>
      )}
      {currentPage === 'Orders Requiring Picking' && permissions['Orderwise Data'] === 'none' && (
        <div>Unauthorized access to Orders Requiring Picking.</div>
      )}

      {/* Customer Payments (child of Orderwise Data) */}
      {currentPage === 'Customer Payments' && permissions['Customer Payments'] !== 'none' && (
        <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
          <div style={{ padding: '0.5rem 2rem 0' }}>
            <button onClick={() => setCurrentPage('Orderwise Data')}>← Back to Orderwise data</button>
          </div>
          <Suspense fallback={<div style={{ padding: '2rem' }}>Loading customer payments…</div>}>
            <OrderwiseCustomerPayments isDark={isDark} accountFilter={pendingCustomerPaymentsAccount} />
          </Suspense>
        </div>
      )}
      {currentPage === 'Customer Payments' && permissions['Customer Payments'] === 'none' && (
        <div style={{ padding: '2rem' }}>Unauthorized access to Customer Payments.</div>
      )}

      {/* Order Locks (child of Orderwise Data, staff-only) */}
      {currentPage === 'Order Locks' && permissions['Orderwise Data'] !== 'none' && (
        isOrderwiseStaff ? (
          <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
            <div style={{ padding: '0.5rem 2rem 0' }}>
              <button onClick={() => setCurrentPage('Orderwise Data')}>← Back to Orderwise data</button>
            </div>
            <Suspense fallback={<div style={{ padding: '2rem' }}>Loading order locks…</div>}>
              <OrderwiseOrderLocks />
            </Suspense>
          </div>
        ) : (
          <div style={{ padding: '2rem' }}>Unauthorized access to Order Locks.</div>
        )
      )}
      {currentPage === 'Order Locks' && permissions['Orderwise Data'] === 'none' && (
        <div>Unauthorized access to Order Locks.</div>
      )}

      {/* Payment Locks (child of Orderwise Data, staff/admin) */}
      {currentPage === 'Payment Locks' && permissions['Orderwise Data'] !== 'none' && (
        (isOrderwiseStaff || permissions['Admin'] !== 'none') ? (
          <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
            <div style={{ padding: '0.5rem 2rem 0' }}>
              <button onClick={() => setCurrentPage('Orderwise Data')}>← Back to Orderwise data</button>
            </div>
            <Suspense fallback={<div style={{ padding: '2rem' }}>Loading payment locks…</div>}>
              <OrderwisePaymentLocks isDark={isDark} isAdmin={permissions['Admin'] !== 'none'} />
            </Suspense>
          </div>
        ) : (
          <div style={{ padding: '2rem' }}>Unauthorized access to Payment Locks.</div>
        )
      )}
      {currentPage === 'Payment Locks' && permissions['Orderwise Data'] === 'none' && (
        <div>Unauthorized access to Payment Locks.</div>
      )}

      <Suspense fallback={null}>
        <PaymentLockNudgeModal
          isDark={isDark}
          socket={yaySocket}
          onOpenPaymentLocks={() => setCurrentPage('Payment Locks')}
        />
      </Suspense>

      {/* Reports (sub-menu hub) */}
      {currentPage === 'Reports' && permissions['Reports'] !== 'none' && (
        <div style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
          <div style={{ padding: '2rem' }}>
            <h2>Reports</h2>
            <p style={{ maxWidth: 720 }}>Business reporting across the different areas of the workplace.</p>
            <div style={{ marginTop: '1rem' }}>
              {permissions['Sales Reports'] !== 'none' && (
                <div style={{ margin: '0.5rem 0' }}>
                  <button onClick={() => setCurrentPage('Sales Reports')}>Sales Reports</button>
                </div>
              )}
              {permissions['Warehouse Reports'] !== 'none' && (
                <div style={{ margin: '0.5rem 0' }}>
                  <button onClick={() => setCurrentPage('Warehouse Reports')}>Warehouse Reports</button>
                </div>
              )}
              {permissions['Other Reports'] !== 'none' && (
                <div style={{ margin: '0.5rem 0' }}>
                  <button onClick={() => setCurrentPage('Other Reports')}>Other Reports</button>
                </div>
              )}
              {permissions['Stock Reports'] !== 'none' && (
                <div style={{ margin: '0.5rem 0' }}>
                  <button onClick={() => setCurrentPage('Stock Reports')}>Stock Reports</button>
                </div>
              )}
              {permissions['Admin Reports'] !== 'none' && (
                <div style={{ margin: '0.5rem 0' }}>
                  <button onClick={() => setCurrentPage('Admin Reports')}>Admin Reports</button>
                </div>
              )}
            </div>
            <div style={{ marginTop: '1rem' }}>
              <button onClick={() => setCurrentPage('Menu')}>← Back to Main Menu</button>
            </div>
          </div>
        </div>
      )}
      {currentPage === 'Reports' && permissions['Reports'] === 'none' && (
        <div>Unauthorized access to Reports.</div>
      )}

      {/* Warehouse Reports (child of Reports) */}
      {currentPage === 'Warehouse Reports' && permissions['Warehouse Reports'] !== 'none' && (
        <div style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
          <div style={{ padding: '2rem' }}>
            <h2>Warehouse Reports</h2>
            <div style={{ marginTop: '1rem', margin: '0.5rem 0' }}>
              <button onClick={() => setCurrentPage('Pick Speed Report')}>Pick Speed Report</button>
            </div>
            <div style={{ marginTop: '1rem' }}>
              <button onClick={() => setCurrentPage('Reports')}>← Back to Reports</button>
            </div>
          </div>
        </div>
      )}
      {currentPage === 'Warehouse Reports' && permissions['Warehouse Reports'] === 'none' && (
        <div>Unauthorized access to Warehouse Reports.</div>
      )}

      {/* Other Reports (child of Reports) */}
      {currentPage === 'Other Reports' && permissions['Other Reports'] !== 'none' && (
        <div style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
          <div style={{ padding: '2rem' }}>
            <h2>Other Reports</h2>
            <div style={{ marginTop: '1rem', margin: '0.5rem 0' }}>
              <button onClick={() => setCurrentPage('Order Check')}>Order Check</button>
            </div>
            <div style={{ marginTop: '1rem' }}>
              <button onClick={() => setCurrentPage('Reports')}>← Back to Reports</button>
            </div>
          </div>
        </div>
      )}
      {currentPage === 'Other Reports' && permissions['Other Reports'] === 'none' && (
        <div>Unauthorized access to Other Reports.</div>
      )}

      {/* Sales Reports (child of Reports) */}
      {currentPage === 'Sales Reports' && permissions['Sales Reports'] !== 'none' && (
        <div style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
          <div style={{ padding: '2rem' }}>
            <h2>Sales Reports</h2>
            <p style={{ color: isDark ? '#94a3b8' : '#64748b' }}>No sales reports have been added yet.</p>
            <div style={{ marginTop: '1rem' }}>
              <button onClick={() => setCurrentPage('Reports')}>← Back to Reports</button>
            </div>
          </div>
        </div>
      )}
      {currentPage === 'Sales Reports' && permissions['Sales Reports'] === 'none' && (
        <div>Unauthorized access to Sales Reports.</div>
      )}

      {/* Stock Reports (child of Reports) */}
      {currentPage === 'Stock Reports' && permissions['Stock Reports'] !== 'none' && (
        <div style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
          <div style={{ padding: '2rem' }}>
            <h2>Stock Reports</h2>
            <p style={{ color: isDark ? '#94a3b8' : '#64748b' }}>No stock reports have been added yet.</p>
            <div style={{ marginTop: '1rem' }}>
              <button onClick={() => setCurrentPage('Reports')}>← Back to Reports</button>
            </div>
          </div>
        </div>
      )}
      {currentPage === 'Stock Reports' && permissions['Stock Reports'] === 'none' && (
        <div>Unauthorized access to Stock Reports.</div>
      )}

      {/* Admin Reports (child of Reports) */}
      {currentPage === 'Admin Reports' && permissions['Admin Reports'] !== 'none' && (
        <div style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
          <div style={{ padding: '2rem' }}>
            <h2>Admin Reports</h2>
            <div style={{ marginTop: '1rem', margin: '0.5rem 0' }}>
              <button onClick={() => setCurrentPage('Admin Pricing Report')}>Admin Pricing Report</button>
            </div>
            <div style={{ marginTop: '1rem' }}>
              <button onClick={() => setCurrentPage('Reports')}>← Back to Reports</button>
            </div>
          </div>
        </div>
      )}
      {currentPage === 'Admin Reports' && permissions['Admin Reports'] === 'none' && (
        <div>Unauthorized access to Admin Reports.</div>
      )}

      {/* Admin Pricing Report (child of Admin Reports) */}
      {currentPage === 'Admin Pricing Report' && permissions['Admin Reports'] !== 'none' && (
        <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
          <div style={{ padding: '0.5rem 1.5rem 0' }}>
            <button onClick={() => setCurrentPage('Admin Reports')}>← Back to Admin Reports</button>
          </div>
          <Suspense fallback={<div style={{ padding: '2rem' }}>Loading admin pricing report…</div>}>
            <AdminPricingReportPage isDark={isDark} />
          </Suspense>
        </div>
      )}
      {currentPage === 'Admin Pricing Report' && permissions['Admin Reports'] === 'none' && (
        <div>Unauthorized access to Admin Pricing Report.</div>
      )}

      {/* Pick Speed Report (child of Warehouse Reports) */}
      {currentPage === 'Pick Speed Report' && permissions['Warehouse Reports'] !== 'none' && (
        <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
          <div style={{ padding: '0.5rem 1.5rem 0' }}>
            <button onClick={() => setCurrentPage('Warehouse Reports')}>← Back to Warehouse Reports</button>
          </div>
          <Suspense fallback={<div style={{ padding: '2rem' }}>Loading pick speed report…</div>}>
            <PickSpeedReportPage isDark={isDark} canRefresh={permissions['Warehouse Reports'] === 'write'} />
          </Suspense>
        </div>
      )}
      {currentPage === 'Pick Speed Report' && permissions['Warehouse Reports'] === 'none' && (
        <div>Unauthorized access to Pick Speed Report.</div>
      )}

      {/* Order Check (child of Other Reports) */}
      {currentPage === 'Order Check' && permissions['Other Reports'] !== 'none' && (
        <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
          <div style={{ padding: '0.5rem 1.5rem 0' }}>
            <button onClick={() => setCurrentPage('Other Reports')}>← Back to Other Reports</button>
          </div>
          <Suspense fallback={<div style={{ padding: '2rem' }}>Loading order check…</div>}>
            <OrderCheckPage isDark={isDark} />
          </Suspense>
        </div>
      )}
      {currentPage === 'Order Check' && permissions['Other Reports'] === 'none' && (
        <div>Unauthorized access to Order Check.</div>
      )}

      {/* OEM Fiche Ingestion (can stay mounted in background) */}
      {(permissions['OEM Fiche Ingestion'] !== 'none' && (currentPage === 'OEM Fiche Ingestion' || ficheRunInBackground)) && (
        <div
          style={
            currentPage === 'OEM Fiche Ingestion'
              ? { flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }
              : { display: 'none' }
          }
        >
          <Suspense fallback={<div style={{ padding: '2rem' }}>Loading Fiche Ingestion...</div>}>
            <OEMFicheIngestion
              isDark={isDark}
              permissions={permissions}
              setCurrentPage={setCurrentPage}
              externalStopSignal={ficheStopSignal}
              onBackgroundPreferenceChange={(enabled) => setFicheRunInBackground(Boolean(enabled))}
              onBackgroundProgressChange={(progress) => setFicheBackgroundProgress(progress || {})}
              onNavigateToMapping={(ctx) => {
                setFicheWorkspaceContext(ctx || {});
                setCurrentPage('OEM Mapping Workspace');
              }}
            />
          </Suspense>
          {currentPage === 'OEM Fiche Ingestion' && (
            <div style={{ padding: '1rem' }}>
              <button onClick={() => setCurrentPage('Menu')}>← Back to Menu</button>
            </div>
          )}
        </div>
      )}
      {currentPage === 'OEM Fiche Ingestion' && permissions['OEM Fiche Ingestion'] === 'none' && (
        <div style={{ padding: '2rem' }}>Unauthorized access to OEM Fiche Ingestion.</div>
      )}

      {/* OEM Mapping Workspace */}
      {currentPage === 'OEM Mapping Workspace' && (permissions['OEM Fiche Ingestion'] !== 'none' || permissions['Admin'] !== 'none') && (
        <div style={{ flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          <Suspense fallback={<div style={{ padding: '2rem' }}>Loading Mapping Workspace...</div>}>
            <OEMMappingWorkspace
              isDark={isDark}
              permissions={permissions}
              initialContext={ficheWorkspaceContext}
              setCurrentPage={setCurrentPage}
            />
          </Suspense>
          <div style={{ padding: '0.5rem 1rem' }}>
            <button onClick={() => { setFicheWorkspaceContext(null); setCurrentPage('Menu'); }}>← Back to Menu</button>
          </div>
        </div>
      )}
      {currentPage === 'OEM Mapping Workspace' && permissions['OEM Fiche Ingestion'] === 'none' && permissions['Admin'] === 'none' && (
        <div style={{ padding: '2rem' }}>Unauthorized access to OEM Mapping Workspace.</div>
      )}

      {currentPage === 'Product Search' && permissions['Product Search'] !== 'none' && (
        <div style={{ flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          <Suspense fallback={<div style={{ padding: '2rem' }}>Loading product search…</div>}>
            <ProductSearch
              permissions={permissions}
              isDark={isDark}
              allowTradePrices={allowTradePrices}
              currentUserAccountNumber={accountNumber}
              currentUserOrderwiseUserId={currentUserProfile?.OrderwiseUserId}
            />
          </Suspense>
        </div>
      )}
      {currentPage === 'Product Search' && permissions['Product Search'] === 'none' && (
        <div style={{ padding: '2rem' }}>Unauthorized access to Product Search.</div>
      )}

      {/* VOIP Sub-Menu */}
      {currentPage === 'VOIP' && permissions['VOIP'] !== 'none' && (
        <div style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
          <div style={{ padding: '2rem' }}>
            <h2>VOIP</h2>
            <p style={{ maxWidth: '48rem' }}>
              View call history, manage VOIP settings, and track inbound/outbound calls from Yay.com.
            </p>
            <div style={{ marginTop: '1rem' }}>
              <div style={{ margin: '0.5rem 0' }}>
                <button onClick={() => setCurrentPage('Phone Wallboard')}>
                  Phone Wallboard
                </button>
              </div>
              <div style={{ margin: '0.5rem 0' }}>
                <button onClick={() => setCurrentPage('Call History')}>
                  Call History
                </button>
              </div>
              <div style={{ margin: '0.5rem 0' }}>
                <button onClick={() => setShowPhoneMappingManager(true)}>
                  Manage Saved Numbers
                </button>
              </div>
              {permissions['Admin'] !== 'none' && (
                <div style={{ margin: '0.5rem 0' }}>
                  <button onClick={() => setCurrentPage('VOIP Settings')}>
                    VOIP Settings
                  </button>
                </div>
              )}
            </div>
            <div style={{ marginTop: '1rem' }}>
              <button onClick={() => setCurrentPage('Menu')}>← Back to Main Menu</button>
            </div>
          </div>
        </div>
      )}
      {currentPage === 'VOIP' && permissions['VOIP'] === 'none' && (
        <div style={{ padding: '2rem' }}>Unauthorized access to VOIP.</div>
      )}

      {/* Phone Wallboard (child of VOIP) */}
      {currentPage === 'Phone Wallboard' && permissions['VOIP'] !== 'none' && (
        <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
          <div style={{ padding: '0.5rem 2rem 0' }}>
            <button onClick={() => setCurrentPage('VOIP')}>← Back to VOIP</button>
          </div>
          <Suspense fallback={<div style={{ padding: '2rem' }}>Loading wallboard…</div>}>
            <PhoneWallboard isDark={isDark} permissions={permissions} />
          </Suspense>
        </div>
      )}
      {currentPage === 'Phone Wallboard' && permissions['VOIP'] === 'none' && (
        <div style={{ padding: '2rem' }}>Unauthorized access to Phone Wallboard.</div>
      )}

      {/* Call History (child of VOIP) */}
      {currentPage === 'Call History' && permissions['VOIP'] !== 'none' && (
        <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
          <div style={{ padding: '0.5rem 2rem 0' }}>
            <button onClick={() => setCurrentPage('VOIP')}>← Back to VOIP</button>
          </div>
          <Suspense fallback={<div style={{ padding: '2rem' }}>Loading call history…</div>}>
            <YayCallHistory
              isDark={isDark}
              permissions={permissions}
              isAdmin={permissions['Admin'] !== 'none'}
              onSearchCustomer={handleVoipSearchCustomer}
            />
          </Suspense>
        </div>
      )}
      {currentPage === 'Call History' && permissions['VOIP'] === 'none' && (
        <div style={{ padding: '2rem' }}>Unauthorized access to Call History.</div>
      )}

      {/* VOIP Settings (admin only, child of VOIP) */}
      {currentPage === 'VOIP Settings' && permissions['VOIP'] !== 'none' && (
        permissions['Admin'] !== 'none' ? (
          <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
            <div style={{ padding: '0.5rem 2rem 0' }}>
              <button onClick={() => setCurrentPage('VOIP')}>← Back to VOIP</button>
            </div>
            <Suspense fallback={<div style={{ padding: '2rem' }}>Loading VOIP settings…</div>}>
              <YaySettings isDark={isDark} permissions={permissions} />
            </Suspense>
          </div>
        ) : (
          <div style={{ padding: '2rem' }}>Unauthorized access to VOIP Settings.</div>
        )
      )}
      {currentPage === 'VOIP Settings' && permissions['VOIP'] === 'none' && (
        <div style={{ padding: '2rem' }}>Unauthorized access to VOIP Settings.</div>
      )}

      {/* Admin*/}
      {currentPage === 'Admin' && permissions['Admin'] !== 'none' && (
        <div style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
          <h2>Admin Panel</h2>
          <PermissionsManager isDark={isDark} />
        </div>
      )}
      {currentPage === 'Admin' && permissions['Admin'] === 'none' && (
        <div>Unauthorized access to Admin.</div>
      )}

      {/* Search Mirrors */}
      {currentPage === 'Search Mirrors' && permissions['Search Mirrors'] !== 'none' && (
        <div style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
          <Suspense fallback={<div style={{ padding: '2rem' }}>Loading Search Mirrors…</div>}>
            <SearchMirrorAdmin isDark={isDark} />
          </Suspense>
        </div>
      )}
      {currentPage === 'Search Mirrors' && permissions['Search Mirrors'] === 'none' && (
        <div style={{ padding: '2rem' }}>Unauthorized access to Search Mirrors.</div>
      )}

      <BackgroundScrapeToast
        progress={ficheBackgroundProgress}
        minimized={ficheToastMinimized}
        onMinimize={() => persistFicheToastMinimized(true)}
        onOpenProgress={() => setCurrentPage('OEM Fiche Ingestion')}
        onStop={() => setFicheStopSignal((v) => v + 1)}
        onOpenMapping={() => {
          setFicheWorkspaceContext({ viewManageFiches: true });
          setCurrentPage('OEM Mapping Workspace');
        }}
      />

    </div>
    </ErrorBoundary>
  );
}